import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@mui/material";
import GoogleMap, {
  type GoogleMapProps,
  type MapCoordinate,
} from "./GoogleMap";
import { useAccordionFilteredMapAssets } from "../../../hooks/useAccordionFilteredMapAssets";
import { useAssetMap, useAssetMapSettings } from "../../../hooks/useAssetMap";
import type {
  Asset,
  CachedPlace,
  Accordion,
  Sector,
  Country,
} from "../../../types/deal";
import { apiPut } from "../../../services/genesisApi/httpsMethods";
import { normalizeAssetData } from "../../../utils/dealValidation";

export interface AssetMapProps extends Omit<
  GoogleMapProps,
  "coordinates" | "trainStations" | "airports"
> {
  /** Single asset or array of assets to display on map */
  assets: Asset | Asset[];
  /** Accordions for filtering assets */
  accordions?: Accordion[];
  /** Show asset count in header */
  showAssetCount?: boolean;
  /** Show asset numbers on markers */
  showAssetNumbers?: boolean;
  /** Custom title for the map */
  title?: string;
  /** Callback when an asset marker is clicked */
  onAssetClick?: (asset: Asset, coordinate: MapCoordinate) => void;

  /** Callback when marker position changes */
  updateNewPosition?: (position: { lat: number; lng: number }) => void;
  dealMapInfo?: {
    sectors: Sector[];
    countries: Country[];
  };
  setMapFullscreenOpen?: (open: boolean) => void;
  mapFullscreenOpen?: boolean;
  hideFullscreenButton?: boolean;
}

const EMPTY_DEAL_MAP_INFO: NonNullable<AssetMapProps["dealMapInfo"]> = {
  sectors: [],
  countries: [],
};

/**
 * AssetMap
 *
 * Requirements implemented:
 * - Cache Google Places (train stations + airports) into DB JSONB fields: cached_train_stations / cached_airports
 * - Allow empty arrays [] (meaning: checked, nothing found within radius)
 * - Radius: 30km
 * - If field is null/undefined => call Places API for THAT asset and save results (or [])
 * - If field is an array (including []) => do NOT call Places API again for that asset+field
 *
 * NOTE: GoogleMap no longer fetches Places; it only renders markers and toggles visibility.
 */
const AssetMap: React.FC<AssetMapProps> = ({
  assets,
  accordions = [],
  showAssetCount = true,
  showAssetNumbers = true,
  title,
  onAssetClick,
  height = 600,
  updateNewPosition,
  dealMapInfo = EMPTY_DEAL_MAP_INFO,
  setMapFullscreenOpen,
  mapFullscreenOpen,
  hideFullscreenButton = false,
  ...mapProps
}) => {
  const [showInitialDealAssets, setShowInitialDealAssets] = useState(true);

  const {
    visibleAccordions,
    selectedAccordions,
    setSelectedAccordions,
    filteredAssets,
  } = useAccordionFilteredMapAssets(assets, accordions, showInitialDealAssets);

  // filteredAssets is always an array from the useMemo above
  const { coordinates } = useAssetMap(filteredAssets);
  const { center, zoom } = useAssetMapSettings(coordinates);

  const assetArray = filteredAssets;

  // Create stable key from asset IDs to prevent infinite loops when asset objects change reference
  // Compute the IDs string directly - useMemo will only recalculate if the string value changes
  const assetIdsKey = useMemo(() => {
    const ids = assetArray
      .map((a) => a.id)
      .filter((id): id is number => id !== undefined)
      .sort((a, b) => a - b);
    return ids.join(",");
  }, [
    // Create a stable dependency by computing the IDs string - React compares by value for primitives
    assetArray
      .map((a) => a.id ?? 0)
      .filter((id) => id !== 0)
      .sort((a, b) => a - b)
      .join(","),
  ]);

  const assetLookup = useMemo(() => {
    const lookup: Record<string | number, Asset> = {};
    assetArray.forEach((a) => {
      if (a.id) lookup[a.id] = a;
    });
    return lookup;
  }, [assetArray]);

  // We need a map instance to construct `google.maps.places.PlacesService`.
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // If the parent doesn't refetch assets after we PUT, keep a per-session override so we don't refetch/re-PUT on refresh.
  // This is also how we consider [] as cached during this session.
  const sessionCacheRef = useRef<
    Map<number, { trainStations: CachedPlace[]; airports: CachedPlace[] }>
  >(new Map());
  const inFlightRef = useRef<Set<number>>(new Set());
  const lastProcessedAssetIdsRef = useRef<string>("");

  const parseCached = useCallback(
    (value: unknown): CachedPlace[] | null | undefined => {
      if (value === null || value === undefined)
        return value as null | undefined;
      if (Array.isArray(value)) return value as CachedPlace[];
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? (parsed as CachedPlace[]) : null;
        } catch {
          return null;
        }
      }
      return null;
    },
    [],
  );

  const getCachedForAsset = useCallback(
    (
      asset: Asset,
    ): {
      trainStations: CachedPlace[] | null | undefined;
      airports: CachedPlace[] | null | undefined;
    } => {
      if (asset.id) {
        const session = sessionCacheRef.current.get(asset.id);
        if (session)
          return {
            trainStations: session.trainStations,
            airports: session.airports,
          };
      }
      return {
        trainStations: parseCached(asset.cached_train_stations),
        airports: parseCached(asset.cached_airports),
      };
    },
    [parseCached], // Remove sessionCacheVersion to prevent loops - sessionCacheRef is stable
  );

  // Build union markers for display (dedupe by place_id or lat/lng).
  // IMPORTANT: This is rendering-only; caching decisions are per-asset via `getCachedForAsset`.
  const cachedMarkers = useMemo(() => {
    const trainStations: MapCoordinate[] = [];
    const airports: MapCoordinate[] = [];
    const seenTrain = new Set<string>();
    const seenAir = new Set<string>();

    for (const asset of assetArray) {
      const cached = getCachedForAsset(asset);
      if (
        Array.isArray(cached.trainStations) &&
        cached.trainStations.length > 0
      ) {
        for (const p of cached.trainStations) {
          const key = String(p.id || `${p.lat},${p.lng}`);
          if (seenTrain.has(key)) continue;
          seenTrain.add(key);
          trainStations.push({
            lat: p.lat,
            lng: p.lng,
            title: p.title,
            id: p.id,
            category: "Train station",
          });
        }
      }
      if (Array.isArray(cached.airports) && cached.airports.length > 0) {
        for (const p of cached.airports) {
          const key = String(p.id || `${p.lat},${p.lng}`);
          if (seenAir.has(key)) continue;
          seenAir.add(key);
          airports.push({
            lat: p.lat,
            lng: p.lng,
            title: p.title,
            id: p.id,
            category: "Airport",
          });
        }
      }
    }

    return { trainStations, airports };
  }, [assetArray, getCachedForAsset]); // Removed sessionCacheVersion to prevent loops

  const handleMarkerClick = useCallback(
    (coordinate: MapCoordinate) => {
      if (onAssetClick && coordinate.id && assetLookup[coordinate.id]) {
        onAssetClick(assetLookup[coordinate.id], coordinate);
      }
    },
    [onAssetClick, assetLookup],
  );

  const onMapLoadInternal = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      setMapInstance(map);
      mapProps.onMapLoad?.(map);
    },
    [mapProps],
  );

  useEffect(() => {
    if (!mapInstance) return;
    if (!window.google?.maps?.places) return;

    // Prevent re-running if asset IDs haven't changed
    if (assetIdsKey === lastProcessedAssetIdsRef.current) {
      return;
    }
    lastProcessedAssetIdsRef.current = assetIdsKey;

    const RADIUS_METERS = 30000;
    const MAX_AIRPORTS = 3;
    const MAX_TRAIN_STATIONS = 20;

    const service = new window.google.maps.places.PlacesService(mapInstance);
    let cancelled = false;

    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const fetchNearby = (
      lat: number,
      lng: number,
      type: "train_station" | "airport",
    ) =>
      new Promise<CachedPlace[]>((resolve) => {
        service.nearbySearch(
          {
            location: new window.google.maps.LatLng(lat, lng),
            radius: RADIUS_METERS,
            type,
          },
          (results, status) => {
            if (
              status !== window.google.maps.places.PlacesServiceStatus.OK ||
              !results
            ) {
              resolve([]);
              return;
            }

            const filtered = results
              .filter((r) => r.geometry?.location)
              .filter((r) => {
                if (type !== "airport") return true;
                const name = (r.name || "").toLowerCase();
                const excludeKeywords = [
                  "transfer",
                  "transfers",
                  "taxi",
                  "cabs",
                  "chauffeur",
                  "executive travel",
                  "limo",
                  "security",
                  "screening",
                  "parking",
                  "car park",
                  "park & ride",
                  "valet",
                  "meet and greet",
                  "meet & greet",
                  "travel",
                  "helipad",
                  "heliport",
                ];
                const notHeliport = !r.types?.includes("heliport");
                const notTaxiRental = !r.types?.some(
                  (t) => t === "taxi_stand" || t === "car_rental",
                );
                const notParkingType = !r.types?.some(
                  (t) => t === "parking" || t === "parking_lot",
                );
                const notParkingName =
                  !name.includes("parking") && !name.includes("car park");
                const hasAirportType = r.types?.includes("airport") || false;
                const hasAirportWord = name.includes("airport");
                const hasInternational = name.includes("international");
                const hasEnoughSignals =
                  hasAirportType && (hasAirportWord || hasInternational);
                const notBusinessService =
                  !r.types?.includes("business_service");
                const notKeywordExcluded = !excludeKeywords.some((k) =>
                  name.includes(k),
                );
                const reviews = (r as any).user_ratings_total || 0;
                const rating = (r as any).rating || 0;
                const hasSignalQuality = reviews >= 5 || rating >= 3.5;
                return (
                  notHeliport &&
                  notTaxiRental &&
                  notParkingType &&
                  notParkingName &&
                  notBusinessService &&
                  notKeywordExcluded &&
                  hasEnoughSignals &&
                  hasSignalQuality
                );
              })
              .filter((r) => {
                if (type !== "train_station") return true;
                const name = (r.name || "").toLowerCase();
                const keywords = [
                  "central",
                  "main",
                  "interchange",
                  "terminal",
                  "station",
                  "rail",
                ];
                const hasKeyword = keywords.some((k) => name.includes(k));
                const reviews = (r as any).user_ratings_total || 0;
                const rating = (r as any).rating || 0;
                const hasReviews =
                  reviews >= 25 || (reviews >= 10 && rating >= 4);
                return hasKeyword || hasReviews;
              })
              .map((r, idx) => ({
                lat: r.geometry!.location!.lat(),
                lng: r.geometry!.location!.lng(),
                title: r.name || `${type} ${idx + 1}`,
                id: (r as any).place_id,
              }));

            const limited =
              type === "airport"
                ? filtered.slice(0, MAX_AIRPORTS)
                : filtered.slice(0, MAX_TRAIN_STATIONS);

            resolve(limited);
          },
        );
      });

    const run = async () => {
      // Sequential + small delay keeps burst under control.
      const missing = assetArray.filter((a) => {
        if (!a.id || !a.latitude || !a.longitude) return false;
        const cached = getCachedForAsset(a);
        return !(
          Array.isArray(cached.trainStations) && Array.isArray(cached.airports)
        );
      });
      console.log("AssetMap places caching: starting", {
        totalAssets: assetArray.length,
        assetsMissingAnyCache: missing.length,
        radiusMeters: RADIUS_METERS,
      });

      for (const asset of assetArray) {
        if (cancelled) return;
        if (!asset.id || !asset.latitude || !asset.longitude) continue;
        if (inFlightRef.current.has(asset.id)) continue;

        const cached = getCachedForAsset(asset);
        const trainCached = Array.isArray(cached.trainStations);
        const airportsCached = Array.isArray(cached.airports);
        if (trainCached && airportsCached) continue;

        inFlightRef.current.add(asset.id);
        try {
          const needsTrain = !trainCached;
          const needsAirports = !airportsCached;

          const [trainStations, airports] = await Promise.all([
            needsTrain
              ? fetchNearby(asset.latitude, asset.longitude, "train_station")
              : Promise.resolve(cached.trainStations as CachedPlace[]),
            needsAirports
              ? fetchNearby(asset.latitude, asset.longitude, "airport")
              : Promise.resolve(cached.airports as CachedPlace[]),
          ]);

          if (cancelled) return;

          // Save ONLY this asset’s results. Empty arrays are valid and should be persisted.
          const updatedAsset: any = { ...asset };
          if (needsTrain) updatedAsset.cached_train_stations = trainStations;
          if (needsAirports) updatedAsset.cached_airports = airports;

          const normalized = normalizeAssetData(updatedAsset);
          if (needsTrain) normalized.cached_train_stations = trainStations;
          if (needsAirports) normalized.cached_airports = airports;

          const resp = await apiPut("asset", asset.id, normalized);
          if (resp.success) {
            sessionCacheRef.current.set(asset.id, { trainStations, airports });
            // Don't update sessionCacheVersion - it's no longer in dependencies and causes loops
          }
        } catch {
          // no-op; don't break map
        } finally {
          inFlightRef.current.delete(asset.id);
        }

        await sleep(75);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [assetIdsKey, mapInstance]); // Use assetIdsKey instead of assetArray to prevent loops. getCachedForAsset is stable.

  // Define handleAccordionToggle BEFORE early return to ensure hooks are always called in same order
  const handleAccordionToggle = useCallback((accordionId: number) => {
    setSelectedAccordions((prev) => {
      if (prev.includes(accordionId)) {
        // Allow deselecting accordions - return filtered array
        return prev.filter((id) => id !== accordionId);
      } else {
        // Add accordion to selection
        return [...prev, accordionId];
      }
    });
  }, []);

  const onMarkerPositionChange = useCallback(
    (position: { lat: number; lng: number }) => {
      if (updateNewPosition && typeof updateNewPosition === "function") {
        updateNewPosition(position);
      }
    },
    [updateNewPosition],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", marginTop: 0 }}>
      {title && (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
          {showAssetCount && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {assetArray.length} asset{assetArray.length === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>
      )}

      <GoogleMap
        {...mapProps}
        coordinates={coordinates}
        center={center}
        zoom={zoom}
        height={height}
        onMarkerClick={handleMarkerClick}
        trainStations={cachedMarkers.trainStations}
        airports={cachedMarkers.airports}
        // Keep toggles enabled; GoogleMap uses them for display only (no fetching).
        showNearbyTrainStations={true}
        showNearbyAirports={true}
        onMapLoad={onMapLoadInternal}
        nearbySearchRadius={30000}
        highlightMotorways={true}
        accordions={visibleAccordions}
        selectedAccordions={new Set(selectedAccordions)}
        onAccordionToggle={handleAccordionToggle}
        showInitialDealAssets={showInitialDealAssets}
        onInitialDealAssetsToggle={setShowInitialDealAssets}
        showAssetNumbers={showAssetNumbers}
        onMarkerPositionChange={onMarkerPositionChange}
        dealMapInfo={dealMapInfo}
        setMapFullscreenOpen={setMapFullscreenOpen}
        hideFullscreenButton={hideFullscreenButton}
        mapFullscreenOpen={mapFullscreenOpen}
        markersNotMoveable={true}
      />
    </Box>
  );
};

export default AssetMap;
